import { Component, computed, inject, resource } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FooterComponent } from '../../shared/components/footer.component';
import { MenuItemComponent } from '../../shared/components/menu-item/menu-item.component';
import { NavBarComponent } from '../../shared/components/nav-bar.component';
import { SideMenuComponent } from '../../shared/components/side-menu/side-menu.component';
import { UserAvatarComponent } from '../../shared/components/user-avatar/user-avatar.component';
import { AccountEndpoints } from './account.routes';
import { firstValueFrom } from 'rxjs';
import { ProfileService } from '../../core/data/profile/profile.service';

@Component({
  selector: 'app-account',
  imports: [
    NavBarComponent,
    RouterModule,
    SideMenuComponent,
    MenuItemComponent,
    UserAvatarComponent,
    FooterComponent,
  ],
  template: `
    <div
      class="min-h-screen flex flex-col bg-gradient-to-br from-slate-100 via-white to-sky-50 overflow-x-hidden"
    >
      <header class="border-b border-slate-200 bg-white/95 backdrop-blur">
        <app-nav-bar [border]="false">
          <div right-content>
            <div class="flex w-full items-center justify-end gap-3">
              <a
                routerLink="/auth/signout"
                class="inline-flex items-center gap-2 rounded-full border border-slate-200/80 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-900"
              >
                Cerrar sesión
              </a>
              <app-user-avatar [name]="name()"></app-user-avatar>
            </div>
          </div>
        </app-nav-bar>
      </header>

      <main class="relative flex-1 overflow-hidden">
        <div
          class="absolute inset-0 -z-10 bg-gradient-to-br from-slate-100 via-white to-sky-50"
        ></div>
        <div
          class="pointer-events-none absolute right-[-8%] top-[20%] h-[28rem] w-[28rem] rounded-full bg-sky-300/20 blur-3xl"
        ></div>
        <div
          class="pointer-events-none absolute left-[-12%] bottom-[-30%] h-[28rem] w-[28rem] rounded-full bg-orange-200/20 blur-3xl"
        ></div>

        @if (suspendedState(); as state) {
        <div class="relative mx-auto flex w-full max-w-2xl flex-col items-center gap-6 px-4 py-20 text-center">
          @if (state === 'deleted') {
          <div class="flex h-20 w-20 items-center justify-center rounded-full bg-slate-100">
            <i class="fa-solid fa-trash-can text-3xl text-slate-400"></i>
          </div>
          <h2 class="text-2xl font-semibold text-slate-900">Cuenta eliminada</h2>
          <p class="max-w-md text-base text-slate-500">
            Tu cuenta ha sido eliminada. Si necesitas asistencia, comunícate con tu proveedor de servicio.
          </p>
          } @else if (state === 'banned') {
          <div class="flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
            <i class="fa-solid fa-shield-halved text-3xl text-red-600"></i>
          </div>
          <h2 class="text-2xl font-semibold text-slate-900">Cuenta bloqueada</h2>
          <p class="max-w-md text-base text-slate-500">
            Tu cuenta ha sido bloqueada por incumplimiento de las políticas del servicio. Comunícate con tu proveedor para más información.
          </p>
          } @else {
          <div class="flex h-20 w-20 items-center justify-center rounded-full bg-amber-100">
            <i class="fa-solid fa-ban text-3xl text-amber-500"></i>
          </div>
          <h2 class="text-2xl font-semibold text-slate-900">Cuenta suspendida</h2>
          <p class="max-w-md text-base text-slate-500">
            Tu cuenta ha sido desactivada temporalmente. Si crees que esto es un error, por favor comunícate con tu proveedor de servicio para más información.
          </p>
          }
          <a
            routerLink="/auth/signout"
            class="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Cerrar sesión
          </a>
        </div>
        } @else {
        <div
          class="relative mx-auto flex w-full max-w-screen-2xl flex-col gap-8 px-4 py-10 sm:px-6 lg:px-10"
        >
          <div class="flex flex-col gap-8 lg:flex-row lg:items-start">
            <div class="lg:hidden -mx-2">
              <div
                class="flex gap-3 overflow-x-auto rounded-3xl border border-slate-200/70 bg-white/95 px-3 py-4 shadow-sm backdrop-blur"
              >
                @for (item of menuLinks; track item.link) {
                <div class="min-w-[11rem]">
                  <menu-item
                    [icon]="item.icon"
                    [title]="item.title"
                    [link]="item.link"
                  />
                </div>
                }
              </div>
            </div>

            <side-menu class="hidden lg:flex lg:w-72 lg:shrink-0">
              @for (item of menuLinks; track item.link) {
              <menu-item
                [icon]="item.icon"
                [title]="item.title"
                [link]="item.link"
              />
              }
            </side-menu>

            <div class="flex-1">
              <router-outlet />
            </div>
          </div>
        </div>
        }
      </main>

      <app-footer />
    </div>
  `,
  styles: ``,
})
export class AccountComponent {
  readonly #profileService = inject(ProfileService);

  profile = resource({
    loader: () => {
      return firstValueFrom(this.#profileService.getProfile());
    },
  });

  readonly name = computed(() => {
    const value = this.profile.value();
    return value?.name ?? '';
  });

  readonly suspendedState = computed(() => {
    const value = this.profile.value();
    const state = value?.state;
    if (state === 'disabled' || state === 'inactive') return 'disabled';
    if (state === 'banned') return 'banned';
    if (state === 'deleted') return 'deleted';
    return null;
  });

  protected readonly menuLinks = [
    {
      title: 'Inicio',
      icon: 'fas fa-home',
      link: AccountEndpoints.Summary,
    },
    {
      title: 'Perfil',
      icon: 'fa-solid fa-address-card',
      link: AccountEndpoints.Profile,
    },
    {
      title: 'Mi Suscripción',
      icon: 'fas fa-tv',
      link: AccountEndpoints.Subscription,
    },
    {
      title: 'Métodos de pago',
      icon: 'fa-regular fa-credit-card',
      link: AccountEndpoints.PaymentMethod,
    },
    {
      title: 'Descargar app',
      icon: 'fa-solid fa-mobile-screen-button',
      link: AccountEndpoints.DownloadApp,
    },
  ] as const;
}
